import Tooltip from '../common/Tooltip.jsx'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { useFirestoreGeneral, useFirestoreGeneralIn } from '../../firebase/useFirestore.js'
import { db, timestamp } from '../../firebase/config.js'
import { v4 as uuid } from 'uuid'
import { client } from '../../hooks/Client.js'
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import { useContext, useState, useEffect } from "react";
import useSettings from "../../hooks/Settings.jsx";
import { Auth } from '../../StateManagment/Auth.js'
import AddItemTableRow from "../common/AddItemTableRow.jsx";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import DeleteModal from "../common/DeleteModal.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import { HOUSEHOLD_FIELDS } from '../../helpers/adressHelpers/householdFields.js'
import resolveReminderContact from '../../helpers/tasks/resolveReminderContact.js'
import { hasCustomMessage, resolveReplyTo } from '../../helpers/tasks/reminderMailText.js'
import ReminderTextModal from './ReminderTextModal.jsx'

// Oude contacttitels in PersonaData, uit het schema van de vaste gegevens (7
// waarden; Firestore `in` kent een limiet van 10). Voor een huishouden is dit de
// terugval tot de migratie; een deelnemer heeft zijn contact alleen hier.
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
const CONTACT_TITLES = [...HOUSEHOLD_FIELDS.Email.legacyTitles, ...HOUSEHOLD_FIELDS.Telefoon.legacyTitles]

/**
 * @typedef {object} TaskReminderDoc
 * @property {string} docid - Firestore-document-id
 * @property {string} ID - Eigen uuid
 * @property {string} TaskID - `Tasks.ID` van de afspraak
 * @property {'email'|'sms'} Type
 * @property {number} DaysBefore - Dagen vóór de afspraak
 * @property {string} [RecipientID] - `Personas.ID` of `Adresses.ID`; ontbreekt tot er een ontvanger gekozen is
 * @property {string} ContactInfo - E-mailadres of telefoonnummer
 * @property {number} Position
 * @property {boolean} Sent - Door de Cloud Function op true gezet na verzenden
 * @property {string} [CustomMessage] - Eigen tekst; leeg of ontbrekend = standaardtekst
 * @property {boolean} [SmsConsent]
 */

/**
 * Tab 'Herinneringen' van een afspraak: per herinnering type, dagen van
 * tevoren, ontvanger, contactgegevens en (bij e-mail) de tekst van de mail.
 * Elke cel schrijft direct naar Firestore; alleen de tekst gaat via een
 * venster met een opslaanknop. Zie docs/features/bezoekherinnering-eigen-tekst.md
 *
 * @param {object} props
 * @param {{ ID: string, AdressID?: string, Title?: string }} props.task - De afspraak waar de herinneringen bij horen
 * @param {Array<{ ID: string, Name: string, Type: string, Email?: string }>} props.recipients - Teamleden die als ontvanger gekozen kunnen worden
 * @param {Array<TaskReminderDoc>} props.reminders - TaskReminders-documenten van deze afspraak, op Position
 */
const TaskReminders = ({task, recipients, reminders}) => {

    // Context
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)

    // State
    const [dragArray, setDragArray] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteName, setDeleteName] = useState('deze taak')
    const [deleteDocid, setDeleteDocid] = useState('')
    const [recipientsPlusAddress, setRecipientsPlusAddress] = useState([])
    // docid van de herinnering waarvan het tekstvenster open staat; '' = dicht.
    const [textModalDocid, setTextModalDocid] = useState('')

    // Hooks
    const { smsRemindersEnabled, compagnyName, email, centralEmail } = useSettings()

    // Firestore
    const adress = useFirestoreGeneral('Adresses', 'ID', task.AdressID || '')
    const contactData = useFirestoreGeneralIn('PersonaData', 'Title', CONTACT_TITLES)

    // Resolve contact info for any recipient (team member, persona, or address)
    const getContactFor = (recipientId, type) => resolveReminderContact({
        recipientId,
        type,
        household: Array.isArray(adress) ? adress[0] : null,
        contactRows: Array.isArray(contactData) ? contactData : [],
        recipients: recipientsPlusAddress || [],
    })

    // Set address info to recipients
    useEffect(() => {
        const baseRecipients = [...(recipients || [])]

        if(!adress || adress.length === 0) {
            setRecipientsPlusAddress(baseRecipients)
            return;
        }

        const adressData = adress[0];

        const adressObject = {
            ID: adressData.ID,
            Name: adressData.Title,
            Type: 'adress',
        }

        setRecipientsPlusAddress([...baseRecipients, adressObject]);

    }, [adress, recipients]);

    // Whenever Firestore data changes, update dragArray state.
    // Skip the update when the snapshot is structurally identical — otherwise
    // every field write produces a new array reference and re-renders the
    // entire DragDropContext, which causes react-beautiful-dnd to remeasure
    // scroll containers and jumps the page.
    useEffect(() => {
        setDragArray((prev) => {
            const next = reminders || [];
            if (Array.isArray(prev) && prev.length === next.length) {
                const same = prev.every((p, i) => {
                    const n = next[i];
                    return (
                        p.docid === n.docid &&
                        p.Type === n.Type &&
                        p.DaysBefore === n.DaysBefore &&
                        p.RecipientID === n.RecipientID &&
                        p.ContactInfo === n.ContactInfo &&
                        p.SmsConsent === n.SmsConsent &&
                        p.Position === n.Position &&
                        p.CustomMessage === n.CustomMessage &&
                        p.Sent === n.Sent
                    );
                });
                if (same) return prev;
            }
            return next;
        });
    }, [reminders]);
    
    // DRAG & DROP: reorder items and update Firestore
    const handleDragEnd = (result) => {
        if (!result.destination) return;
    
        const items = Array.from(dragArray);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);
    
        setDragArray(items);
    
        items.forEach((dragItem, idx) => {
            db.collection('TaskReminders')
            .doc(dragItem.docid)
            .update({ Position: idx + 1 });
        });
    
        setSaved('flex');
    };

    // Add Reminder
    const addReminder = async () => {

        await db.collection('TaskReminders')
        .add({
            ID: uuid(),
            TaskID: task.ID,
            Type: 'email',
            DaysBefore: 1,
            Recipient: '',
            ContactInfo: '',
            Position: reminders ? reminders.length + 1 : 1,
            CreatedBy: auth?.ID || 'unknown',
            CreatedAt: timestamp,
            CompagnyID: client,
            Compagny: client,
            Sent: false,
        })
        .then(() => {
            setSaved('flex')
        })
    }

    // Type Handler
    const typeHandler = (e, reminder) => {
        const value = e.target.options[e.target.selectedIndex].value
        const docid = reminder.docid

        const updates = { Type: value }
        const autoContact = getContactFor(reminder.RecipientID, value)
        if (autoContact) {
            updates.ContactInfo = autoContact
        }

        db.collection('TaskReminders')
        .doc(docid)
        .update(updates)
        .then(() => {
            setSaved('flex')
        })

    }

    // SMS Consent Handler — staff attest the recipient agreed to receive SMS.
    // Stored with who/when for audit; the daily sender requires SmsConsent === true.
    const smsConsentHandler = (e, reminder) => {
        const checked = e.target.checked

        db.collection('TaskReminders')
        .doc(reminder.docid)
        .update({
            SmsConsent: checked,
            SmsConsentBy: auth?.ID || 'unknown',
            SmsConsentAt: timestamp,
        })
        .then(() => {
            setSaved('flex')
        })
    }

    // Days Before Handler
    const daysBeforeHandler = (e) => {
        const value = parseInt(e.target.value)
        const docid = e.target.dataset.docid

        db.collection('TaskReminders')
        .doc(docid)
        .update({
            DaysBefore: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Delete Modal Handler
    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const deletename = e.currentTarget.dataset.deletename

        setDeleteDocid(docid)
        setDeleteName(deletename)
        setDeleteModal(true)
    }

    // Delete Reminder
    const deleteReminder = async () => {

        await db.collection('TaskReminders')
        .doc(deleteDocid)
        .delete()
        .then(() => {
            setSaved('flex')
        })

    }

    // Recipient Handler
    const recipientHandler = async (e, reminder) => {
        const value = e.target.options[e.target.selectedIndex].value
        const docid = reminder.docid

        const updates = { RecipientID: value }
        const autoContact = getContactFor(value, reminder.Type)
        if (autoContact) {
            updates.ContactInfo = autoContact
        }

        db.collection('TaskReminders')
        .doc(docid)
        .update(updates)
        .then(() => {
            setSaved('flex')
        })
    }

    // Naam van een ontvanger (teamlid of huishouden) voor de aanhef in het tekstvenster.
    const recipientNameFor = (recipientId) => (recipientsPlusAddress || []).find((r) => r.ID === recipientId)?.Name || ''

    // Het venster leest de LIVE herinnering uit de props, niet een kopie van het
    // klikmoment. AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
    const textModalReminder = textModalDocid ? (reminders || []).find((r) => r.docid === textModalDocid) : null

    // Contact Info Handler
    const contactInfoHandler = (e) => {
        const value = e.target.value
        const docid = e.target.dataset.docid

        db.collection('TaskReminders')
        .doc(docid)
        .update({
            ContactInfo: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

  return (
    <div>
        <ReadOnlyGuard edit="tasks.edit">
        {(canEdit) => (
        <>
        <table>
            <thead>
                <tr>
                <th></th>
                <th>TYPE</th>
                <th>AANTAL DAGEN VAN TEVOREN</th>
                <th>ONTVANGER</th>
                <th>E-MAILADRES/TELEFOON</th>
                <th>TEKST</th>
                <th>VERWIJDEREN</th>
                </tr>
            </thead>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="reports" direction="vertical">
                {(provided) => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {dragArray && dragArray.map((reminder, index) => (
                        <Draggable key={reminder.ID} draggableId={reminder.ID} index={index}>
                        {(provided) => (
                            <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            >
                            <td>
                                <DragIndicatorOutlinedIcon />
                            </td>

                            <td>
                                {/* Gestuurd, niet `defaultValue`: die leest React alleen bij het
                                    aanhaken, en de optie 'SMS' hangt aan een instelling die uit een
                                    eigen Firestore-read komt. Is die er nog niet, dan bestaat de
                                    optie nog niet en zou een SMS-herinnering zich blijvend als
                                    'E-mail' voordoen.
                                    AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                                <select name="" id="" value={reminder.Type} data-docid={reminder.docid} onChange={(e) => typeHandler(e, reminder)}>
                                    <option value="email">E-mail</option>
                                    {smsRemindersEnabled && <option value="sms">SMS</option>}
                                </select>
                            </td>

                            <td>
                                <input type="number" defaultValue={reminder.DaysBefore} data-docid={reminder.docid} onBlur={daysBeforeHandler}/>
                            </td>

                            <td>
                                <select name="" id="" value={reminder.RecipientID} data-docid={reminder.docid} onChange={(e) => recipientHandler(e, reminder)}>
                                    <option value="">-- Selecteer ontvanger --</option>
                                    {recipientsPlusAddress && recipientsPlusAddress.map(recipient => (
                                        <option key={recipient.ID} value={recipient.ID}>{recipient.Name} ({recipient.Type})</option>
                                    ))}
                                </select>
                            </td>

                            <td>
                                <input
                                    key={reminder.ContactInfo || ''}
                                    type={reminder.Type === 'email' ? 'email' : 'tel'}
                                    defaultValue={reminder.ContactInfo}
                                    data-docid={reminder.docid}
                                    onBlur={contactInfoHandler}
                                />
                                {reminder.Type === 'sms' && (
                                <label className="sms-consent-label">
                                    <input
                                        type="checkbox"
                                        checked={reminder.SmsConsent === true}
                                        onChange={(e) => smsConsentHandler(e, reminder)}
                                    />
                                    Toestemming voor SMS ontvangen
                                </label>
                                )}
                            </td>

                            <td>
                                {reminder.Type !== 'sms' && (
                                <div className="reminder-text-cell">
                                    <span className={hasCustomMessage(reminder.CustomMessage) ? 'reminder-text-status reminder-text-status-custom' : 'reminder-text-status'}>
                                        {hasCustomMessage(reminder.CustomMessage) ? 'Aangepast' : 'Standaard'}
                                    </span>
                                    {canEdit && (
                                    <Tooltip content={reminder.Sent === true ? 'Al verzonden' : 'Tekst aanpassen'} top='-60px' width={'30px'}>
                                    <button
                                        type="button"
                                        className="reminder-text-button"
                                        aria-label="Tekst aanpassen"
                                        disabled={reminder.Sent === true}
                                        onClick={() => setTextModalDocid(reminder.docid)}
                                    >
                                        <EditNoteOutlinedIcon className="reminder-text-icon" />
                                    </button>
                                    </Tooltip>
                                    )}
                                </div>
                                )}
                            </td>

                            <td>
                                {canEdit && (
                                <Tooltip content={'Taak verwijderen'} top='-60px' width={'30px'}>
                                <DeleteOutlinedIcon
                                    className='table-delete-icon'
                                    data-id={reminder.ID}
                                    data-docid={reminder.docid}
                                    data-deletename={reminder.Title}
                                    onClick={deleteModalHandler}
                                />
                                </Tooltip>
                                )}
                            </td>
                            </tr>
                        )}
                        </Draggable>
                    ))}
                    {provided.placeholder}
                    </tbody>
                )}
                </Droppable>
            </DragDropContext>
        </table>
        {canEdit && (
        <AddItemTableRow
        toolTipContent={'Reminder toevoegen'}
        onClick={addReminder}
        />
        )}
        <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteReminder}
        deleteName={deleteName}
        />
        {textModalReminder && (
        <ReminderTextModal
        reminder={textModalReminder}
        task={task}
        recipientName={recipientNameFor(textModalReminder.RecipientID)}
        companyName={compagnyName || ''}
        replyTo={resolveReplyTo(email, centralEmail)}
        modalOpen
        setModalOpen={(open) => { if (!open) setTextModalDocid('') }}
        />
        )}
        </>
        )}
        </ReadOnlyGuard>
    </div>
  )
}

export default TaskReminders
